/**
 * FramedImage — Displays an image fully (no crop) inside a fixed frame.
 *
 * Uses the "letterbox with blurred backdrop" pattern:
 *   • Backdrop layer: same image `object-cover` + blur + slight zoom to
 *     fill any empty space with a soft, on-brand tint (works for
 *     portrait, landscape and square uploads alike).
 *   • Foreground layer: same image `object-contain` so the entire
 *     picture is always visible regardless of aspect ratio.
 *
 * This solves the vendor complaint that portrait / rotated shots were
 * being cropped in gallery tiles.
 */
import React from "react";

export default function FramedImage({
  src,
  alt = "",
  aspect = "aspect-square",
  className = "",
  rounded = "rounded-xl",
  testId,
  onClick,
}) {
  if (!src) return null;
  return (
    <div
      className={`relative ${aspect} w-full overflow-hidden ${rounded} border border-border/60 bg-muted ${
        onClick ? "cursor-pointer" : ""
      } ${className}`}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Blurred backdrop — fills the frame edges */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-60"
      />
      {/* Foreground — the actual image, fully visible */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="relative h-full w-full object-contain"
      />
    </div>
  );
}
